import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { z } from "zod";
import API_ROUTES from "~/api-routes";
import API_ROUTES_V2 from "~/api-routes-v2";
import apiService from "~/services/service";
import useFileUpload from "~/utils/useFileUpload";
import { getPeriodFromFileYearMonth } from "~/utils/fileUtils";
import useAttachmentFilter from "~/hooks/useAttachmentFilter";
import {
  buildCategoryFoldersFromDocuments,
  getDocumentCategoryCode,
} from "~/utils/categoryFolders";

const useDirectorAttachments = ({ directorData }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(true);
  const [attachmentConfirmModal, setAttachmentConfirmModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [temporaryMessage, setTemporaryMessage] = useState("");
  const [restoreMessage, setRstoreMessage] = useState("");
  const [isAttachmentLoader, setIsAttachmentLoader] = useState(false);
  const [selectedFile, setSelectedFile] = useState<Record<string, any>>({});
  const [rows, setRows] = useState(10);
  const [first, setFirst] = useState(0);
  const { directorId } = useParams();
  const [filesData, setFilesdata] = useState([]);
  const [loading, setLoading] = useState(true);
  const { uploadFile, updateTag, uuidToUlid, getFileVersion } = useFileUpload();
  const { id: userId, role } = useSelector((state) => state.login);
  const [filterData, setFilterData] = useState({});
  const [categoryFolders, setCategoryFolders] = useState<
    { id: string; code: string; name: string; isCategoryFolder?: boolean }[]
  >([]);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: string;
    code: string;
    name: string;
    isCategoryFolder?: boolean;
  } | null>(null);
  const [chipData, setChipData] = useState({});
  const removeFnRef = useRef(null);
  const {
    data,
    loading: filterLoading,
    error,
    onFilterChange,
    onFilterApply,
    onSortOrderChange,
    allData,
    onPageChange: filterPageChange,
    currentPage,
  } = useAttachmentFilter();

  const handleChangePage = (e) => {
    setRows(e.rows);
    setFirst(e.first);
  };
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const fileSupported = [
    "xls",
    "csv",
    "xml",
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "txt",
    "doc",
    "docx",
    "xlsx",
  ];
  const getNameForFile = () => {
    const name =
      directorData.preferred_name ||
      [directorData?.name, directorData?.last_name].filter(Boolean).join(" ");
    return { name };
  };
  const fetchDirectorAttachments = async (skipCache = false) => {
    setLoading(true);
    const { data } = await apiService(API_ROUTES_V2.FETCH_DOCUMENTS, "GET", {
      replace: {
        id: directorId,
        type: "DIRECTOR",
      },
      skipCache,
    });
    setFilesdata(data);
    setCategoryFolders(buildCategoryFoldersFromDocuments(data));
    setIsDataAvailable(data.length > 0);
    setLoading(false);
  };
  useEffect(() => {
    fetchDirectorAttachments();
  }, []);
  const uploadFileHandler = async (files) => {
    setIsFileUploaded(false);
    try {
      setLoading(true);
      const formData = new FormData();
      const fileOutput = [];
      const attachments = [];
      let tags, metadata;
      for (let i = 0; i < files.length; i++) {
        const file = files[i]?.file;
        formData.append("file", file);
        let fileData = {
          fileName: file?.fileName,
          category: file?.category,
          subCategory: file?.subcategory,
        };
        if (file?.description) {
          fileData.description = file?.description;
        }
        fileOutput.push(fileData);
        formData.append("filesData", JSON.stringify(fileOutput));
        tags = {
          entity_id: uuidToUlid(directorData?.entityData?.id),
          country: directorData?.entityData?.country,
          temporary_file: "false",
          director: uuidToUlid(directorId),
          category: file?.category,
          subCategory: file?.subcategory,
          year: new Date().getFullYear().toString(),
          ...(getPeriodFromFileYearMonth(file?.fileYearMonth) && {
            period: getPeriodFromFileYearMonth(file.fileYearMonth),
          }),
        };
        const version = `${await getFileVersion(tags)}`;
        metadata = {
          original_file_name: encodeURIComponent(file.name),
          file_size: file.size.toString(),
          file_type: file.type,
          user: userId,
          file_name: file.fileName,
          version,
          created_at: new Date().toISOString(),
          file_object_type: "DIRECTOR",
          ...(file?.description && {
            description: encodeURIComponent(file?.description),
          }),
        };
        const uploadData = await uploadFile(file, file.fileName, {
          entityId: directorData?.entityData?.id,
          serviceLineCode: "ENTITIES_PLUS",
          fileName: file.name,
        });
        metadata = { ...metadata, url: uploadData.url };
        attachments.push({
          url: uploadData.url,
          fileName: uploadData.id,
          name: file.fileName,
        });
        await updateTag(uploadData.id, tags, metadata);
      }
      // Create audit entry for uploaded/updated files
      await apiService(API_ROUTES.ADD_DOCUMENT_AUDIT, "POST", {
        body: { attachments },
        replace: { id: directorId, type: "DIRECTOR" },
        schema: z.object({
          id: z.string().uuid(),
          type: z.enum(["DIRECTOR"]),
        }),
      });
      setTimeout(async () => {
        setIsFileUploaded(true);
        setFilesdata(files);
        setSuccessMessage("File uploaded successfully");
        fetchDirectorAttachments(true); // skipCache after upload
      }, 2000);
    } catch (e) {
      console.error(e);
      setIsFileUploaded(false);
    } finally {
      setOpenDialog(false);
    }
  };
  const handleDeleteDocument = (file) => {
    setAttachmentConfirmModal(true);
    setSelectedFile(file);
  };
  const selectedItems = Object.values(filterData || {}).filter(
    (value) =>
      !["category", "service_line", "entities", "year"].includes(value?.id),
  );
  const handleConfirmDelete = async () => {
    setIsAttachmentLoader(true);
    setRstoreMessage("");
    setTemporaryMessage("");
    const { status } = await apiService(
      API_ROUTES_V2.DELETE_ATTACHMENT,
      "DELETE",
      {
        body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
        replace: { relatedId: directorId, relatedType: "DIRECTOR" },
      },
    );
    if (status) {
      setTimeout(async () => {
        setLoading(true);
        if (selectedItems?.length) {
          await onFilterApply();
        } else {
          await fetchDirectorAttachments(true);
        }
        setAttachmentConfirmModal(false);
        setTemporaryMessage(
          `${selectedFile?.displayName || selectedFile?.name} has been deleted.`,
        );
        setIsAttachmentLoader(false);
      }, 2000);
    }
  };
  const handleRestoreAttachment = async () => {
    setTemporaryMessage("");
    setRstoreMessage("");
    setLoading(true);
    const { status } = await apiService(
      API_ROUTES_V2.RESTORE_ATTACHMENT,
      "POST",
      {
        body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
      },
    );
    if (status) {
      setTimeout(async () => {
        if (selectedItems?.length) {
          await onFilterApply();
        } else {
          await fetchDirectorAttachments(true);
        }
        setRstoreMessage(
          `${selectedFile?.displayName || selectedFile?.name} has been restored.`,
        );
      }, 2000);
    }
  };
  const handleFilterChange = (data) => {
    const categories = Object.entries(data?.category || {}).map(
      ([key, value]) => ({
        category: key,
        subCategories: value?.map((sub) =>
          sub.includes("/") ? sub.split("/").pop() : sub,
        ),
      }),
    );
    setChipData(categories);
    onFilterChange({
      categories,
      typeId: directorId,
      type: "DIRECTOR",
    });
  };

  useEffect(() => {
    if (selectedItems?.length) {
      onFilterApply();
    } else {
      fetchDirectorAttachments();
    }
  }, [filterData]);

  const handleRemoveFilter = (id) => {
    if (removeFnRef.current) {
      const { value: updatedValue, grouped } = removeFnRef.current(id);
      setFilterData(updatedValue);
      handleFilterChange(grouped);
    }
  };

  const handleCategoryFolderClick = (folder: {
    id: string;
    code: string;
    name: string;
  }) => {
    setSelectedCategory(folder);
  };

  const displayFiles = selectedCategory
    ? filesData.filter(
        (f) => getDocumentCategoryCode(f) === selectedCategory.code,
      )
    : filesData;

  return {
    onPageChange: handleChangePage,
    loading,
    isDataAvailable,
    filesData,
    rows,
    first,
    handleDeleteDocument,
    openDialog,
    setOpenDialog,
    isFileUploaded,
    setIsFileUploaded,
    attachmentConfirmModal,
    setAttachmentConfirmModal,
    successMessage,
    setSuccessMessage,
    temporaryMessage,
    setTemporaryMessage,
    restoreMessage,
    setRstoreMessage,
    isAttachmentLoader,
    setIsAttachmentLoader,
    uploadFileHandler,
    handleRestoreAttachment,
    handleConfirmDelete,
    fileSupported,
    getNameForFile,
    role,
    selectedItems,
    filterData,
    setFilterData,
    chipData,
    setChipData,
    removeFnRef,
    handleRemoveFilter,
    handleFilterChange,
    data,
    filterLoading,
    error,
    onFilterChange,
    onFilterApply,
    onSortOrderChange,
    allData,
    filterPageChange,
    currentPage,
    fetchDirectorAttachments,
    categoryFolders,
    selectedCategory,
    setSelectedCategory,
    handleCategoryFolderClick,
    displayFiles,
  };
};

export default useDirectorAttachments;
